// src/components/opportunity/OpportunityOverviewTab.tsx
//
// Overview tab. Customer snapshot (editable with confirm), product type
// (change reruns pricing and keeps compatible fields), status, and the
// active revision summary.

import { useEffect, useState } from "react";
import {
  OPPORTUNITY_STATUS_LABELS,
  OPPORTUNITY_STATUSES,
  type Opportunity,
  type OpportunityPricingSnapshot,
  type OpportunityRevision,
  type OpportunityStatus,
} from "@/types/opportunity";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import {
  archiveOpportunity,
  deleteOpportunity,
  updateOpportunityStatus,
} from "@/lib/opportunity/opportunityClient";
import { OpportunityOwnerTransferPanel } from "@/components/opportunity/OpportunityOwnerTransferPanel";
import { OpportunityLeadCrmPanel } from "@/components/opportunity/OpportunityLeadCrmPanel";
import { OpportunityCustomerCard } from "@/components/opportunity/OpportunityCustomerCard";
import { OpportunityProductTypeField } from "@/components/opportunity/OpportunityProductTypeField";
import { OpportunityFulfillmentField } from "@/components/opportunity/OpportunityFulfillmentField";
import {
  OpportunityOverviewCard,
  OpportunityReadOnlyRow,
  OpportunityStat,
} from "@/components/opportunity/OpportunityOverviewPrimitives";
import { canViewOrgWideSalesRecords } from "@/lib/sales/orgRecordVisibility";
import { formatOpportunityCurrency, formatOpportunityPct } from "@/lib/opportunity/formatCurrency";
import { userCanDeleteOpportunity } from "@/lib/opportunity/opportunityDeleteAccess";
import { OpportunitySchedulingNotesSection } from "@/components/opportunity/OpportunitySchedulingNotesSection";
import { OrgFormStartAction } from "@/components/opportunity/OrgFormStartAction";
import { resolveManagerPresetFormatKey } from "@/lib/opportunity/formatRegistry";
import { resolveFormatDocumentSlots } from "@/lib/opportunity/formatDocumentSlotResolver";
import { listOpportunityDocuments } from "@/lib/opportunity/opportunityDocumentsClient";
import {
  formatMissingRequiredDocumentSlotsError,
  missingRequiredDocumentSlots,
} from "@/lib/opportunity/requiredDocumentSlots";
import {
  parseMissingRequiredDocumentSlots,
} from "@/lib/opportunity/opportunitySignedDocumentGate";
import { OpportunitySignedConfirmModal } from "@/components/opportunity/OpportunitySignedConfirmModal";

export type OpportunityOverviewTabProps = {
  opportunity: Opportunity;
  activeSnapshot: OpportunityPricingSnapshot | null;
  revisions: OpportunityRevision[];
  onChanged: () => void;
  onArchived?: () => void;
  onDeleted?: () => void;
};

export function OpportunityOverviewTab(props: OpportunityOverviewTabProps) {
  const { opportunity, activeSnapshot, revisions, onChanged, onArchived, onDeleted } = props;
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusSelect, setStatusSelect] = useState(opportunity.status);
  const [signedConfirmOpen, setSignedConfirmOpen] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [lifecycleMsg, setLifecycleMsg] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    setStatusSelect(opportunity.status);
  }, [opportunity.status, opportunity.id]);

  function revertStatusSelect() {
    setStatusSelect(opportunity.status);
  }

  async function applyStatusChange(next: OpportunityStatus) {
    setStatusBusy(true);
    setStatusError(null);
    const r = await updateOpportunityStatus({ opportunity_id: opportunity.id, status: next });
    setStatusBusy(false);
    if (!r.ok) {
      revertStatusSelect();
      setSignedConfirmOpen(false);
      const missing = parseMissingRequiredDocumentSlots(r.payload);
      setStatusError(
        missing.length > 0 ? formatMissingRequiredDocumentSlotsError(missing) : r.error,
      );
      return;
    }
    setSignedConfirmOpen(false);
    onChanged();
  }

  async function changeStatus(next: OpportunityStatus) {
    if (next === opportunity.status) return;
    setStatusSelect(next);
    setStatusError(null);

    if (next === "signed" && opportunity.status !== "signed") {
      const formatKey = resolveManagerPresetFormatKey(opportunity.product_type);
      const slotDefs = await resolveFormatDocumentSlots({
        formatKey,
        productType: opportunity.product_type,
      });
      const docsR = await listOpportunityDocuments({ opportunityId: opportunity.id });
      const documents = docsR.ok ? (docsR.data.documents ?? []) : [];
      const missing = missingRequiredDocumentSlots({ slotDefs, documents });
      if (missing.length > 0) {
        revertStatusSelect();
        setStatusError(formatMissingRequiredDocumentSlotsError(missing));
        return;
      }
      setSignedConfirmOpen(true);
      return;
    }

    await applyStatusChange(next);
  }

  async function handleArchive() {
    if (!window.confirm("Archive this opportunity? It will be hidden from the active list.")) return;
    setLifecycleBusy(true);
    setLifecycleMsg(null);
    const r = await archiveOpportunity(opportunity.id);
    setLifecycleBusy(false);
    if (!r.ok) {
      setLifecycleMsg({ text: r.error, ok: false });
      return;
    }
    setLifecycleMsg({ text: "Opportunity archived.", ok: true });
    onArchived?.();
  }

  async function handleDelete() {
    if (
      !window.confirm(
        "Permanently delete this opportunity and all revisions/documents? This cannot be undone.",
      )
    ) {
      return;
    }
    setLifecycleBusy(true);
    setLifecycleMsg(null);
    const r = await deleteOpportunity(opportunity.id);
    setLifecycleBusy(false);
    if (!r.ok) {
      setLifecycleMsg({ text: r.error, ok: false });
      return;
    }
    onDeleted?.();
  }

  const activeRevision = revisions.find((r) => r.id === opportunity.active_revision_id) ?? null;
  const canManageOrgWide =
    auth?.status === "ready" && canViewOrgWideSalesRecords((...r) => auth.hasRole(...r));
  const canDelete =
    auth?.status === "ready" &&
    auth.session?.user?.id != null &&
    userCanDeleteOpportunity(auth.session.user.id, (...r) => auth.hasRole(...r), opportunity);

  return (
    <div className="space-y-4">
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
        <OpportunityCustomerCard
          opportunity={opportunity}
          onChanged={onChanged}
          setMessage={setLifecycleMsg}
        />

        <OpportunityOverviewCard title="Opportunity">
          <OpportunityProductTypeField
            opportunity={opportunity}
            activeSnapshot={activeSnapshot}
            disabled={lifecycleBusy}
            onChanged={onChanged}
            setMessage={setLifecycleMsg}
          />
          <OpportunityFulfillmentField
            opportunity={opportunity}
            disabled={lifecycleBusy}
            onChanged={onChanged}
            setMessage={setLifecycleMsg}
          />
          <OpportunityReadOnlyRow
            label="Lead link"
            value={opportunity.lead_id ? opportunity.lead_id : "(unlinked)"}
            mono={!!opportunity.lead_id}
          />
          <div className="flex items-center gap-2 py-1 min-w-0">
            <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 w-24 shrink-0">
              Status
            </span>
            <select
              value={statusSelect}
              onChange={(e) => void changeStatus(e.target.value as OpportunityStatus)}
              disabled={statusBusy || signedConfirmOpen}
              className="min-w-0 flex-1 max-w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
            >
              {OPPORTUNITY_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {OPPORTUNITY_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            {statusBusy ? (
              <span className="text-xs text-gray-500 dark:text-zinc-400">Saving…</span>
            ) : null}
          </div>
          {statusError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 mt-1 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
              {statusError}
            </div>
          ) : null}
          {scopedOrgId && auth?.session?.user?.id ? (
            <div className="mt-3 pt-2 border-t border-gray-100 dark:border-zinc-800">
              <OpportunityOwnerTransferPanel
                organizationId={scopedOrgId}
                currentUserId={auth.session.user.id}
                opportunity={opportunity}
                disabled={lifecycleBusy}
                onTransferred={onChanged}
                setMessage={setLifecycleMsg}
              />
            </div>
          ) : null}
        </OpportunityOverviewCard>
      </section>

      <OpportunitySchedulingNotesSection opportunity={opportunity} onSaved={onChanged} />

      {scopedOrgId ? (
        <OpportunityLeadCrmPanel
          opportunity={opportunity}
          organizationId={scopedOrgId}
          canManageOrgWide={canManageOrgWide}
          onChanged={onChanged}
          setMessage={setLifecycleMsg}
        />
      ) : null}

      {lifecycleMsg ? (
        <div
          className={
            "rounded-lg border px-3 py-2 text-xs " +
            (lifecycleMsg.ok
              ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100"
              : "border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100")
          }
        >
          {lifecycleMsg.text}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <OrgFormStartAction sourceType="opportunity" opportunityId={opportunity.id} />
        <button
          type="button"
          onClick={() => void handleArchive()}
          disabled={lifecycleBusy || !!opportunity.archived_at}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {opportunity.archived_at ? "Archived" : "Archive"}
        </button>
        {canDelete ? (
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={lifecycleBusy}
            className="px-3 py-1.5 rounded-lg border border-red-300 text-xs text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40 disabled:opacity-50"
          >
            Delete
          </button>
        ) : null}
      </div>

      <OpportunityOverviewCard title={activeRevision ? `Active revision · #${activeRevision.revision_number}` : "Active revision"}>
        {activeSnapshot ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <OpportunityStat label="Total cost" value={formatOpportunityCurrency(activeSnapshot.total_cost)} />
            <OpportunityStat
              label="Sale price"
              value={formatOpportunityCurrency(activeSnapshot.total_sale_price)}
              emphasis
            />
            <OpportunityStat
              label="Commission"
              value={`${formatOpportunityCurrency(activeSnapshot.commission_amount)} (${formatOpportunityPct(
                activeSnapshot.commission_pct,
              )})`}
            />
            <OpportunityStat
              label="Margin"
              value={`${formatOpportunityCurrency(activeSnapshot.margin_amount)} (${formatOpportunityPct(
                activeSnapshot.margin_pct,
              )})`}
            />
            <OpportunityStat
              label={`Deposit (${formatOpportunityPct(activeSnapshot.deposit_pct)})`}
              value={formatOpportunityCurrency(activeSnapshot.deposit_amount)}
            />
            <OpportunityStat
              label="Balance on completion"
              value={formatOpportunityCurrency(activeSnapshot.remaining_balance)}
            />
          </div>
        ) : (
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            No pricing snapshot yet. Open the <strong>Pricing</strong> tab and save a
            revision.
          </p>
        )}
      </OpportunityOverviewCard>
      <OpportunitySignedConfirmModal
        open={signedConfirmOpen}
        busy={statusBusy}
        onConfirm={() => void applyStatusChange("signed")}
        onCancel={() => {
          setSignedConfirmOpen(false);
          revertStatusSelect();
        }}
      />
    </div>
  );
}
